import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Input,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Steps,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  LoadingOutlined,
  SaveOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { RcFile, UploadFile } from 'antd/es/upload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { PageShell } from '@/components/PageShell/PageShell';
import { ERPTable, type ERPColumnDef } from '@/components/DataTable/ERPTable';
import { ROUTES } from '@/constants/routes';
import {
  importService,
  type CreateImportTemplatePayload,
  type ImportActionSummary,
  type ImportDuplicateAction,
  type ImportEntity,
  type ImportJobStatus,
  type ImportPreviewCell,
  type ImportUploadResponse,
  type ImportValidationIssue,
} from '@/services/import.service';
import { getApiError } from '@/utils/apiError';
import { formatDateTime, formatNumber } from '@/utils/format';

type WizardStep = 0 | 1 | 2 | 3 | 4;
type ImportPhase = 'dryrun' | 'commit' | null;
type PreviewTableRow = { key: string; row: number } & Record<string, ImportPreviewCell | number | string>;
type MappingRow = { key: string; fileColumn: string; sampleValue: string; mappedField?: string };

interface FieldDefinition {
  key: string;
  label: string;
  required?: boolean;
  aliases: string[];
}

interface EntityConfig {
  title: string;
  label: string;
  cardDescription: string;
  sampleFileName: string;
  sampleContent: string;
  fields: FieldDefinition[];
}

const { Text, Title } = Typography;

const STEPS = ['Tải file', 'Ánh xạ cột', 'Kiểm tra trước nhập', 'Đang nhập', 'Kết quả'];

const TEMPLATE_SOURCE_OPTIONS = [
  { label: 'TivaCloud', value: 'tivacloud' },
  { label: 'WinGold', value: 'wingold' },
  { label: 'KiotViet', value: 'kiotviet' },
  { label: 'Khác', value: 'custom' },
] as const;

const ENTITY_CONFIG: Record<ImportEntity, EntityConfig> = {
  products: {
    title: 'Import sản phẩm',
    label: 'sản phẩm',
    cardDescription: 'CSV với cột: SKU, Tên, Loại vàng, Trọng lượng, Giá bán...',
    sampleFileName: 'mau-import-san-pham.csv',
    sampleContent: [
      'Tên sản phẩm,Mã SP,Loại vàng,Trọng lượng (g),Giá bán,Mã vạch,Nhóm hàng,Ghi chú',
      'Nhẫn trơn 9999,RING-001,9999,3.75,7500000,893850000001,Nhẫn,Nhẫn trơn mẫu',
      'Lắc tay 18K,BRAC-001,18K,5.20,5200000,893850000002,Lắc tay,Lắc tay nữ',
    ].join('\n'),
    fields: [
      { key: 'name', label: 'Tên sản phẩm', required: true, aliases: ['ten san pham', 'tên sản phẩm', 'ten hang', 'tên hàng', 'name'] },
      { key: 'sku', label: 'Mã sản phẩm', required: true, aliases: ['ma sp', 'mã sp', 'ma san pham', 'mã sản phẩm', 'ma hang', 'mã hàng', 'sku', 'code'] },
      { key: 'gold_type', label: 'Loại vàng', required: true, aliases: ['loai vang', 'loại vàng', 'tuoi vang', 'tuổi vàng', 'gold type', 'goldtype'] },
      { key: 'weight', label: 'Trọng lượng', aliases: ['trong luong', 'trọng lượng', 'khoi luong', 'khối lượng', 'trong luong g', 'trọng lượng g', 'weight'] },
      { key: 'price', label: 'Giá bán', aliases: ['gia ban', 'giá bán', 'gia', 'giá', 'price', 'sell price'] },
      { key: 'barcode', label: 'Mã vạch', aliases: ['ma vach', 'mã vạch', 'barcode'] },
      { key: 'category', label: 'Nhóm hàng', aliases: ['nhom hang', 'nhóm hàng', 'danh muc', 'danh mục', 'category'] },
      { key: 'note', label: 'Ghi chú', aliases: ['ghi chu', 'ghi chú', 'mo ta', 'mô tả', 'note', 'description'] },
    ],
  },
  customers: {
    title: 'Import khách hàng',
    label: 'khách hàng',
    cardDescription: 'CSV với cột: Tên, Số điện thoại, Email, Địa chỉ, Ghi chú...',
    sampleFileName: 'mau-import-khach-hang.csv',
    sampleContent: [
      'Tên khách hàng,Số điện thoại,Email,Địa chỉ,Ghi chú',
      'Nguyễn Văn A,0909123456,a@gmail.com,123 Lý Tự Trọng,Khách VIP',
      'Trần Thị B,0918987654,b@yahoo.com,456 Nguyễn Huệ,Khách thân thiết',
    ].join('\n'),
    fields: [
      { key: 'name', label: 'Tên khách hàng', required: true, aliases: ['ten khach hang', 'tên khách hàng', 'ho ten', 'họ tên', 'name'] },
      { key: 'phone', label: 'Số điện thoại', required: true, aliases: ['so dien thoai', 'số điện thoại', 'dien thoai', 'điện thoại', 'phone'] },
      { key: 'email', label: 'Email', aliases: ['email', 'e-mail', 'mail'] },
      { key: 'address', label: 'Địa chỉ', aliases: ['dia chi', 'địa chỉ', 'address'] },
      { key: 'note', label: 'Ghi chú', aliases: ['ghi chu', 'ghi chú', 'note'] },
    ],
  },
  suppliers: {
    title: 'Import nhà cung cấp',
    label: 'nhà cung cấp',
    cardDescription: 'CSV với cột: Tên, Điện thoại, Email, Địa chỉ, Mã số thuế...',
    sampleFileName: 'mau-import-nha-cung-cap.csv',
    sampleContent: [
      'Tên nhà cung cấp,Số điện thoại,Email,Địa chỉ,Mã số thuế,Ghi chú',
      'Vàng bạc SJC,02838223366,sjc@sjc.com.vn,123 Lê Lợi,SJC001,Nhà cung cấp chính',
      'Kim hoàn Mi Hồng,02839996677,mihong@mihong.vn,456 Trần Hưng Đạo,MH002,Đối tác lâu năm',
    ].join('\n'),
    fields: [
      { key: 'name', label: 'Tên nhà cung cấp', required: true, aliases: ['ten nha cung cap', 'tên nhà cung cấp', 'ten ncc', 'tên ncc', 'name'] },
      { key: 'phone', label: 'Số điện thoại', required: true, aliases: ['so dien thoai', 'số điện thoại', 'dien thoai', 'điện thoại', 'phone'] },
      { key: 'email', label: 'Email', aliases: ['email', 'e-mail', 'mail'] },
      { key: 'address', label: 'Địa chỉ', aliases: ['dia chi', 'địa chỉ', 'address'] },
      { key: 'tax_code', label: 'Mã số thuế', aliases: ['ma so thue', 'mã số thuế', 'tax code', 'tax_code'] },
      { key: 'note', label: 'Ghi chú', aliases: ['ghi chu', 'ghi chú', 'note'] },
    ],
  },
};

function normalizeText(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function formatCell(value: ImportPreviewCell): string {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
}

function formatFileSize(bytes: number): string {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function triggerTextDownload(fileName: string, content: string, mimeType: string): void {
  const blob = new Blob(['\uFEFF', content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function triggerBlobDownload(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function buildAutoMapping(columns: string[], fields: FieldDefinition[]): Record<string, string> {
  const result: Record<string, string> = {};
  for (const column of columns) {
    const normalizedColumn = normalizeText(column);
    const matchedField = fields.find((field) => field.aliases.includes(normalizedColumn));
    if (matchedField) {
      result[column] = matchedField.key;
    }
  }
  return result;
}

function applyTemplateMapping(
  columns: string[],
  currentMapping: Record<string, string>,
  templateMapping: Record<string, string>,
  supportedFieldKeys: Set<string>,
): Record<string, string> {
  const next = { ...currentMapping };
  const templateEntries = new Map(
    Object.entries(templateMapping).map(([key, value]) => [normalizeText(key), value]),
  );

  for (const column of columns) {
    const mappedField = templateEntries.get(normalizeText(column));
    if (mappedField && supportedFieldKeys.has(mappedField)) {
      next[column] = mappedField;
    }
  }

  return next;
}

function toPreviewRows(uploadData: ImportUploadResponse | null): PreviewTableRow[] {
  if (!uploadData) return [];
  return uploadData.preview.map((row, index) => {
    const base: PreviewTableRow = { key: `row-${index}`, row: index + 2 };
    uploadData.columns.forEach((column, columnIndex) => {
      base[column] = row[columnIndex] ?? null;
    });
    return base;
  });
}

function getStatusLabel(status: ImportJobStatus['status']): string {
  switch (status) {
    case 'pending':
      return 'Đang chờ';
    case 'processing':
      return 'Đang xử lý';
    case 'awaiting_confirmation':
      return 'Chờ xác nhận';
    case 'completed':
      return 'Hoàn thành';
    case 'failed':
      return 'Thất bại';
    default:
      return status;
  }
}

function getStatusTagColor(status: ImportJobStatus['status']): string {
  switch (status) {
    case 'pending':
      return 'default';
    case 'processing':
      return 'processing';
    case 'awaiting_confirmation':
      return 'warning';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

function getStatusIcon(status: ImportJobStatus['status']) {
  switch (status) {
    case 'pending':
      return <ClockCircleOutlined />;
    case 'processing':
      return <LoadingOutlined spin />;
    case 'awaiting_confirmation':
      return <WarningOutlined />;
    case 'completed':
      return <CheckCircleOutlined />;
    case 'failed':
      return <WarningOutlined />;
    default:
      return <ClockCircleOutlined />;
  }
}

function buildActionSummaryItems(actionSummary: ImportActionSummary | null) {
  return [
    { key: 'create', label: 'Sẽ tạo mới', value: actionSummary?.create_rows ?? 0 },
    { key: 'update', label: 'Sẽ cập nhật', value: actionSummary?.update_rows ?? 0 },
    { key: 'skip', label: 'Sẽ bỏ qua', value: actionSummary?.skip_rows ?? 0 },
    { key: 'warning', label: 'Cảnh báo', value: actionSummary?.warning_rows ?? 0 },
    { key: 'error', label: 'Lỗi', value: actionSummary?.error_rows ?? 0 },
  ];
}

function getMutationErrorMessage(error: unknown, fallback: string): string {
  return getApiError(error)?.message ?? (error instanceof Error ? error.message : fallback);
}

export function ImportHubPage() {
  return (
    <PageShell title="Import dữ liệu">
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <ImportCard
            title="Import sản phẩm"
            desc={ENTITY_CONFIG.products.cardDescription}
            path={ROUTES.imports.products}
          />
        </Col>
        <Col xs={24} md={8}>
          <ImportCard
            title="Import khách hàng"
            desc={ENTITY_CONFIG.customers.cardDescription}
            path={ROUTES.imports.customers}
          />
        </Col>
        <Col xs={24} md={8}>
          <ImportCard
            title="Import nhà cung cấp"
            desc={ENTITY_CONFIG.suppliers.cardDescription}
            path={ROUTES.imports.suppliers}
          />
        </Col>
      </Row>
    </PageShell>
  );
}

function ImportCard({ title, desc, path }: { title: string; desc: string; path: string }) {
  const navigate = useNavigate();
  return (
    <Card hoverable style={{ minHeight: 180 }} onClick={() => navigate(path)}>
      <Space direction="vertical">
        <FileExcelOutlined style={{ fontSize: 32, color: '#a4193d' }} />
        <Text strong style={{ fontSize: 16 }}>{title}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>{desc}</Text>
        <Button type="link" style={{ padding: 0 }}>Bắt đầu →</Button>
      </Space>
    </Card>
  );
}

export function ImportProductsPage() {
  return <ImportWizard entity="products" />;
}

export function ImportCustomersPage() {
  return <ImportWizard entity="customers" />;
}

export function ImportSuppliersPage() {
  return <ImportWizard entity="suppliers" />;
}

interface ImportWizardProps {
  entity: ImportEntity;
  embedded?: boolean;
  onClose?: () => void;
}

export function ImportWizard({ entity, embedded = false, onClose }: ImportWizardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const config = ENTITY_CONFIG[entity];
  const supportedFieldKeys = useMemo(() => new Set(config.fields.map((field) => field.key)), [config.fields]);

  const [step, setStep] = useState<WizardStep>(0);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [uploadData, setUploadData] = useState<ImportUploadResponse | null>(null);
  const [columnMapping, setColumnMapping] = useState<Record<string, string>>({});
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [duplicateAction, setDuplicateAction] = useState<ImportDuplicateAction>('upsert');
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState<ImportPhase>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateSourceSystem, setTemplateSourceSystem] = useState<string>('custom');

  const templatesQuery = useQuery({
    queryKey: ['import', 'templates'],
    queryFn: () => importService.listTemplates(),
    retry: 0,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => importService.upload(file),
  });

  const createTemplateMutation = useMutation({
    mutationFn: (payload: CreateImportTemplatePayload) => importService.createTemplate(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['import', 'templates'] });
    },
  });

  const startImportMutation = useMutation({
    mutationFn: importService.startImport,
  });

  const deleteJobMutation = useMutation({
    mutationFn: (id: string) => importService.deleteJob(id),
  });

  const statusQuery = useQuery({
    queryKey: ['import', 'status', currentJobId],
    queryFn: () => importService.getStatus(currentJobId ?? ''),
    enabled: Boolean(currentJobId),
    retry: 0,
    refetchInterval: isPolling ? 2000 : false,
  });

  const activeJob = statusQuery.data ?? startImportMutation.data ?? null;

  const isDryRunRunning = currentPhase === 'dryrun' && !!activeJob && (activeJob.status === 'pending' || activeJob.status === 'processing');
  const isDryRunReady = currentPhase === 'dryrun' && activeJob?.status === 'awaiting_confirmation';
  const isDryRunFailed = currentPhase === 'dryrun' && activeJob?.status === 'failed';

  useEffect(() => {
    if (!activeJob) return;
    if (currentPhase === 'dryrun') {
      if (activeJob.status === 'awaiting_confirmation' || activeJob.status === 'failed') {
        setIsPolling(false);
      }
      return;
    }
    if (currentPhase === 'commit' && (activeJob.status === 'completed' || activeJob.status === 'failed')) {
      setIsPolling(false);
      setStep(4);
    }
  }, [activeJob, currentPhase]);

  const previewRows = useMemo(() => toPreviewRows(uploadData), [uploadData]);

  const previewColumns = useMemo<ERPColumnDef<PreviewTableRow>[]>(() => {
    if (!uploadData) return [];
    return [
      { key: 'row', title: 'Dòng', dataIndex: 'row', width: 70, alwaysVisible: true },
      ...uploadData.columns.map((column) => ({
        key: column,
        title: column,
        dataIndex: column,
        render: (value: ImportPreviewCell) => formatCell(value),
      })),
    ];
  }, [uploadData]);

  const mappingRows = useMemo<MappingRow[]>(() => {
    if (!uploadData) return [];
    const firstPreviewRow = uploadData.preview[0] ?? [];
    return uploadData.columns.map((column, index) => ({
      key: column,
      fileColumn: column,
      sampleValue: formatCell(firstPreviewRow[index] ?? null),
      mappedField: columnMapping[column],
    }));
  }, [columnMapping, uploadData]);

  const selectedMappings = useMemo(
    () => Object.fromEntries(Object.entries(columnMapping).filter(([, value]) => value)),
    [columnMapping],
  );

  const missingRequiredFields = useMemo(
    () => config.fields.filter((field) => field.required && !Object.values(selectedMappings).includes(field.key)),
    [config.fields, selectedMappings],
  );

  const templatesForEntity = useMemo(
    () => (templatesQuery.data ?? []).filter((template) => template.entity === entity),
    [entity, templatesQuery.data],
  );

  const errorRows = activeJob?.validation_result?.errors ?? [];

  const errorColumns: ERPColumnDef<ImportValidationIssue>[] = [
    { key: 'row', title: 'Dòng', dataIndex: 'row', width: 70, alwaysVisible: true },
    { key: 'column', title: 'Cột', dataIndex: 'column' },
    {
      key: 'severity',
      title: 'Mức độ',
      dataIndex: 'severity',
      render: (value: ImportValidationIssue['severity']) => (
        <Tag color={value === 'error' ? 'error' : 'warning'}>
          {value === 'error' ? 'Lỗi' : 'Cảnh báo'}
        </Tag>
      ),
    },
    { key: 'message', title: 'Thông báo', dataIndex: 'message' },
  ];

  const resetWizard = () => {
    setStep(0);
    setFileList([]);
    setUploadData(null);
    setColumnMapping({});
    setSelectedTemplateId(undefined);
    setDuplicateAction('upsert');
    setCurrentJobId(null);
    setCurrentPhase(null);
    setIsPolling(false);
    setSaveTemplateOpen(false);
    setTemplateName('');
    setTemplateSourceSystem('custom');
    uploadMutation.reset();
    startImportMutation.reset();
    createTemplateMutation.reset();
    deleteJobMutation.reset();
  };

  const handleSampleDownload = () => {
    triggerTextDownload(config.sampleFileName, config.sampleContent, 'text/csv;charset=utf-8');
  };

  const handleUpload = async (file: RcFile) => {
    setFileList([file as UploadFile]);
    setCurrentJobId(null);
    setCurrentPhase(null);
    setIsPolling(false);
    setSelectedTemplateId(undefined);
    startImportMutation.reset();

    try {
      const response = await uploadMutation.mutateAsync(file);
      setUploadData(response);
      setColumnMapping(buildAutoMapping(response.columns, config.fields));
      message.success('Đã tải file và phân tích xem trước thành công');
    } catch (error) {
      setUploadData(null);
      setColumnMapping({});
      message.error(getMutationErrorMessage(error, 'Không thể tải file import'));
    }
  };

  const handleTemplateSelect = (value: string) => {
    setSelectedTemplateId(value);
    const template = templatesForEntity.find((item) => item.template_id === value);
    if (!template || !uploadData) return;
    setColumnMapping((current) => applyTemplateMapping(uploadData.columns, current, template.mapping, supportedFieldKeys));
    message.success(`Đã áp dụng mẫu: ${template.name}`);
  };

  const handleMappingChange = (fileColumn: string, mappedField: string) => {
    setSelectedTemplateId(undefined);
    setColumnMapping((current) => {
      const next = { ...current };
      if (!mappedField) {
        delete next[fileColumn];
        return next;
      }
      next[fileColumn] = mappedField;
      return next;
    });
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      message.error('Vui lòng nhập tên mẫu');
      return;
    }
    if (Object.keys(selectedMappings).length === 0) {
      message.error('Chưa có ánh xạ hợp lệ để lưu mẫu');
      return;
    }

    try {
      const created = await createTemplateMutation.mutateAsync({
        name: templateName.trim(),
        source_system: templateSourceSystem,
        entity,
        mapping: selectedMappings,
      });
      setSaveTemplateOpen(false);
      setTemplateName('');
      setSelectedTemplateId(created.template_id);
      message.success('Đã lưu mẫu ánh xạ');
    } catch (error) {
      message.error(getMutationErrorMessage(error, 'Không thể lưu mẫu ánh xạ'));
    }
  };

  const handleStartDryRun = async () => {
    if (!uploadData) return;
    if (missingRequiredFields.length > 0) {
      message.error('Còn trường bắt buộc chưa được ánh xạ');
      return;
    }

    try {
      const job = await startImportMutation.mutateAsync({
        upload_id: uploadData.upload_id,
        entity,
        mapping: selectedMappings,
        duplicate_action: duplicateAction,
        is_dryrun: true,
      });
      setCurrentJobId(job.import_id);
      setCurrentPhase('dryrun');
      setIsPolling(true);
      message.success('Đã tạo job kiểm tra trước khi nhập');
    } catch (error) {
      message.error(getMutationErrorMessage(error, 'Không thể bắt đầu kiểm tra trước khi nhập'));
    }
  };

  const handleConfirmImport = async () => {
    if (!currentJobId) return;

    try {
      const job = await startImportMutation.mutateAsync({
        import_job_id: currentJobId,
        is_dryrun: false,
      });
      setCurrentJobId(job.import_id);
      setCurrentPhase('commit');
      setIsPolling(true);
      setStep(3);
      message.success('Đã xác nhận import vào hệ thống');
    } catch (error) {
      message.error(getMutationErrorMessage(error, 'Không thể xác nhận import'));
    }
  };

  const handleCancelDryRun = async (nextStep: WizardStep = 2) => {
    if (!currentJobId) {
      setCurrentPhase(null);
      setIsPolling(false);
      setStep(nextStep);
      return;
    }

    try {
      await deleteJobMutation.mutateAsync(currentJobId);
      setCurrentJobId(null);
      setCurrentPhase(null);
      setIsPolling(false);
      startImportMutation.reset();
      setStep(nextStep);
      message.success('Đã huỷ job kiểm tra trước khi nhập');
    } catch (error) {
      message.error(getMutationErrorMessage(error, 'Không thể huỷ job kiểm tra'));
    }
  };

  const handleDownloadErrorCsv = async () => {
    if (!activeJob?.import_id) return;
    try {
      const blob = await importService.downloadErrorCsv(activeJob.import_id);
      triggerBlobDownload(`import-errors-${activeJob.import_id}.csv`, blob);
    } catch (error) {
      message.error(getMutationErrorMessage(error, 'Không thể tải báo cáo lỗi CSV'));
    }
  };

  const usedFieldKeys = Object.entries(columnMapping).reduce<Record<string, string>>((acc, [column, field]) => {
    if (field) acc[field] = column;
    return acc;
  }, {});

  const content = (
    <>
      <Card>
        <Steps current={step} size="small" items={STEPS.map((title) => ({ title }))} style={{ marginBottom: 24 }} />

        {step === 0 && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Alert
              type="info"
              showIcon
              message={`Upload CSV hoặc Excel chứa ${config.label}. Có thể tải file mẫu để điền đúng cột.`}
              action={<Button icon={<DownloadOutlined />} onClick={handleSampleDownload}>Tải file mẫu</Button>}
            />
            <Upload.Dragger
              accept=".csv,.xlsx,.xls"
              maxCount={1}
              fileList={fileList}
              beforeUpload={(file) => {
                void handleUpload(file as RcFile);
                return false;
              }}
              onRemove={() => {
                resetWizard();
                return true;
              }}
              disabled={uploadMutation.isPending}
            >
              <p>
                <UploadOutlined style={{ fontSize: 32, color: '#a4193d' }} />
              </p>
              <p>Kéo file vào đây hoặc click để chọn</p>
              <p style={{ fontSize: 12, color: '#888' }}>CSV / XLSX / XLS · Tối đa 10MB</p>
            </Upload.Dragger>

            {uploadMutation.isPending && <Progress percent={50} status="active" showInfo={false} />}

            {uploadData && (
              <>
                <Descriptions
                  bordered
                  column={1}
                  size="small"
                  items={[
                    { key: 'file', label: 'Tên file', children: uploadData.file_name },
                    { key: 'size', label: 'Kích thước', children: formatFileSize(uploadData.file_size) },
                    { key: 'rows', label: 'Tổng số dòng', children: formatNumber(uploadData.total_rows) },
                    { key: 'parser', label: 'Parser', children: uploadData.parser_type },
                  ]}
                />

                <Space direction="vertical" style={{ width: '100%' }} size={8}>
                  <Text strong>Xem trước 5 dòng đầu</Text>
                  {previewRows.length > 0 ? (
                    <ERPTable<PreviewTableRow>
                      tableKey={`import-preview-${entity}`}
                      size="small"
                      dataSource={previewRows}
                      rowKey="key"
                      pagination={false}
                      hideSettingsButton
                      columns={previewColumns}
                    />
                  ) : (
                    <Empty description="File không có dữ liệu xem trước" />
                  )}
                </Space>

                <Space>
                  <Button onClick={resetWizard}>Chọn file khác</Button>
                  <Button type="primary" onClick={() => setStep(1)}>Tiếp tục</Button>
                </Space>
              </>
            )}
          </Space>
        )}

        {step === 1 && uploadData && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Alert type="info" showIcon message="Ánh xạ cột file vào các trường hệ thống. Trường bắt buộc phải được chọn trước khi tiếp tục." />

            <Space wrap>
              <Select
                allowClear
                placeholder="Áp dụng mẫu có sẵn"
                style={{ minWidth: 280 }}
                value={selectedTemplateId}
                loading={templatesQuery.isLoading}
                options={templatesForEntity.map((template) => ({
                  label: `${template.name}${template.is_built_in ? ' · Hệ thống' : ' · Tuỳ chỉnh'}`,
                      value: template.template_id,
                }))}
                onChange={(value) => {
                  if (!value) {
                    setSelectedTemplateId(undefined);
                    return;
                  }
                  handleTemplateSelect(value);
                }}
              />
              <Button icon={<SaveOutlined />} onClick={() => setSaveTemplateOpen(true)}>
                Lưu mẫu hiện tại
              </Button>
            </Space>

            {missingRequiredFields.length > 0 && (
              <Alert
                type="warning"
                showIcon
                message={`Trường bắt buộc chưa ánh xạ: ${missingRequiredFields.map((field) => field.label).join(', ')}`}
              />
            )}

            <ERPTable<MappingRow>
              tableKey={`import-field-mapping-${entity}`}
              size="small"
              pagination={false}
              hideSettingsButton
              dataSource={mappingRows}
              rowKey="key"
              columns={[
                { key: 'fileColumn', title: 'Cột file', dataIndex: 'fileColumn', alwaysVisible: true },
                { key: 'sampleValue', title: 'Giá trị mẫu', dataIndex: 'sampleValue', render: (value: string) => <Text type="secondary">{value}</Text> },
                {
                  key: 'mappedField',
                  title: 'Trường hệ thống',
                  render: (_value: unknown, row: MappingRow) => (
                    <Select
                      style={{ width: '100%' }}
                      value={row.mappedField ?? ''}
                      onChange={(value) => handleMappingChange(row.fileColumn, value)}
                      options={[
                        { label: '— Bỏ qua —', value: '' },
                        ...config.fields.map((field) => ({
                          label: `${field.label}${field.required ? ' *' : ''}`,
                          value: field.key,
                          disabled: Boolean(usedFieldKeys[field.key] && usedFieldKeys[field.key] !== row.fileColumn),
                        })),
                      ]}
                    />
                  ),
                },
              ] as ERPColumnDef<MappingRow>[]}
            />

            <Space>
              <Button onClick={() => setStep(0)}>Quay lại</Button>
              <Button type="primary" onClick={() => setStep(2)} disabled={missingRequiredFields.length > 0}>
                Tiếp tục
              </Button>
            </Space>
          </Space>
        )}

        {step === 2 && uploadData && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {!currentPhase && (
              <>
                <Alert
                  type="info"
                  showIcon
                  message="Bước này sẽ chạy kiểm tra trước khi nhập. Dữ liệu chưa được ghi vào hệ thống cho đến khi bạn xác nhận."
                />

                <Descriptions
                  bordered
                  column={1}
                  size="small"
                  items={[
                    { key: 'file', label: 'Tên file', children: uploadData.file_name },
                    { key: 'rows', label: 'Tổng số dòng', children: formatNumber(uploadData.total_rows) },
                    { key: 'mapped', label: 'Số cột đã ánh xạ', children: `${Object.keys(selectedMappings).length}/${uploadData.columns.length}` },
                    { key: 'required', label: 'Trường bắt buộc', children: missingRequiredFields.length === 0 ? 'Đã đủ' : 'Chưa đủ' },
                  ]}
                />

                <Space direction="vertical" size={8}>
                  <Text strong>Xử lý dữ liệu trùng</Text>
                  <Radio.Group value={duplicateAction} onChange={(event) => setDuplicateAction(event.target.value as ImportDuplicateAction)}>
                    <Space direction="vertical">
                      <Radio value="upsert">Cập nhật dữ liệu cũ bằng dữ liệu mới</Radio>
                      <Radio value="skip">Bỏ qua các dòng đã tồn tại</Radio>
                      <Radio value="create">Luôn tạo mới</Radio>
                    </Space>
                  </Radio.Group>
                </Space>

                <Space>
                  <Button onClick={() => setStep(1)}>Quay lại</Button>
                  <Button type="primary" loading={startImportMutation.isPending} onClick={handleStartDryRun}>
                    Kiểm tra trước khi nhập
                  </Button>
                </Space>
              </>
            )}

            {isDryRunRunning && activeJob && (
              <>
                <Alert
                  type="info"
                  showIcon
                  message={`Đang kiểm tra file: ${getStatusLabel(activeJob.status)}`}
                />
                <div>
                  <Space align="center" style={{ marginBottom: 12 }}>
                    {getStatusIcon(activeJob.status)}
                    <Title level={5} style={{ margin: 0 }}>Đang kiểm tra trước khi nhập</Title>
                  </Space>
                  <Progress percent={activeJob.progress ?? 0} status="active" />
                </div>
                <Descriptions
                  bordered
                  column={1}
                  size="small"
                  items={[
                    { key: 'processed', label: 'Đã xử lý', children: `${formatNumber(activeJob.processed_rows)} / ${formatNumber(activeJob.total_rows)}` },
                    { key: 'warning', label: 'Cảnh báo tạm thời', children: formatNumber(activeJob.warning_rows) },
                    { key: 'error', label: 'Lỗi tạm thời', children: formatNumber(activeJob.error_rows) },
                  ]}
                />
              </>
            )}

            {isDryRunReady && activeJob && (
              <>
                <Alert
                  type={activeJob.error_rows > 0 ? 'warning' : 'success'}
                  showIcon
                  message="Kết quả kiểm tra trước khi nhập. Dữ liệu chưa được ghi vào hệ thống. Nếu tiếp tục, hệ thống sẽ thực hiện đúng các thay đổi dưới đây."
                />

                <Descriptions
                  bordered
                  column={1}
                  size="small"
                  items={[
                    { key: 'created', label: 'Job dry-run tạo lúc', children: activeJob.created_at ? formatDateTime(activeJob.created_at) : '—' },
                    { key: 'ready', label: 'Chờ xác nhận từ', children: activeJob.awaiting_confirmation_at ? formatDateTime(activeJob.awaiting_confirmation_at) : '—' },
                    { key: 'status', label: 'Trạng thái', children: <Tag color={getStatusTagColor(activeJob.status)}>{getStatusLabel(activeJob.status)}</Tag> },
                  ]}
                />

                <Descriptions
                  bordered
                  column={1}
                  size="small"
                  items={buildActionSummaryItems(activeJob.action_summary).map((item) => ({
                    key: item.key,
                    label: item.label,
                    children: formatNumber(item.value),
                  }))}
                />

                {errorRows.length > 0 ? (
                  <>
                    <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                      <Text strong>Chi tiết lỗi / cảnh báo</Text>
                      <Button icon={<DownloadOutlined />} onClick={handleDownloadErrorCsv}>
                        Tải CSV lỗi
                      </Button>
                    </Space>
                    <ERPTable<ImportValidationIssue>
                      tableKey={`import-dryrun-errors-${entity}`}
                      size="small"
                      pagination={false}
                      hideSettingsButton
                      dataSource={errorRows}
                      rowKey={(row) => `${row.row}-${row.column}-${row.message}`}
                      columns={errorColumns}
                    />
                  </>
                ) : (
                  <Empty description="Không có lỗi hoặc cảnh báo" />
                )}

                <Space>
                  <Button onClick={() => { void handleCancelDryRun(1); }}>Quay lại chỉnh mapping</Button>
                  <Button onClick={() => { void handleCancelDryRun(2); }} loading={deleteJobMutation.isPending}>
                    Huỷ kiểm tra
                  </Button>
                  <Button type="primary" loading={startImportMutation.isPending} onClick={handleConfirmImport}>
                    Tiến hành nhập
                  </Button>
                </Space>
              </>
            )}

            {isDryRunFailed && activeJob && (
              <>
                <Alert
                  type="error"
                  showIcon
                  message={activeJob.error ?? 'Kiểm tra trước khi nhập thất bại'}
                />
                <Space>
                  <Button onClick={() => { void handleCancelDryRun(1); }}>Quay lại chỉnh mapping</Button>
                  <Button onClick={() => { void handleCancelDryRun(2); }} loading={deleteJobMutation.isPending}>
                    Huỷ job kiểm tra
                  </Button>
                </Space>
              </>
            )}
          </Space>
        )}

        {step === 3 && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Alert
              type={activeJob?.status === 'failed' ? 'error' : 'info'}
              showIcon
              message={activeJob ? `Trạng thái hiện tại: ${getStatusLabel(activeJob.status)}` : 'Đang khởi tạo job import vào hệ thống...'}
            />

            <div>
              <Space align="center" style={{ marginBottom: 12 }}>
                {activeJob ? getStatusIcon(activeJob.status) : <LoadingOutlined spin />}
                <Title level={5} style={{ margin: 0 }}>
                  {activeJob ? getStatusLabel(activeJob.status) : 'Đang chờ backend xác nhận import'}
                </Title>
              </Space>
              <Progress percent={activeJob?.progress ?? 0} status={activeJob?.status === 'failed' ? 'exception' : 'active'} />
            </div>

            {activeJob && (
              <Descriptions
                bordered
                column={1}
                size="small"
                items={[
                  { key: 'processed', label: 'Đã xử lý', children: `${formatNumber(activeJob.processed_rows)} / ${formatNumber(activeJob.total_rows)}` },
                  { key: 'success', label: 'Thành công', children: formatNumber(activeJob.success_rows) },
                  { key: 'warning', label: 'Cảnh báo', children: formatNumber(activeJob.warning_rows) },
                  { key: 'error', label: 'Lỗi', children: formatNumber(activeJob.error_rows) },
                ]}
              />
            )}
          </Space>
        )}

        {step === 4 && activeJob && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            <Alert
              type={activeJob.status === 'failed' ? 'error' : activeJob.error_rows > 0 ? 'warning' : 'success'}
              showIcon
              icon={activeJob.status === 'failed' ? <WarningOutlined /> : <CheckCircleOutlined />}
              message={
                activeJob.status === 'failed'
                  ? `Import thất bại: ${activeJob.error ?? 'Lỗi không xác định'}`
                  : activeJob.error_rows > 0
                    ? `Import hoàn tất với ${formatNumber(activeJob.error_rows)} dòng lỗi`
                    : `Hoàn tất — đã import ${formatNumber(activeJob.success_rows)} ${config.label}`
              }
            />

            <Descriptions
              bordered
              column={1}
              size="small"
              items={[
                { key: 'status', label: 'Trạng thái', children: <Tag color={getStatusTagColor(activeJob.status)}>{getStatusLabel(activeJob.status)}</Tag> },
                { key: 'created', label: 'Tạo lúc', children: activeJob.created_at ? formatDateTime(activeJob.created_at) : '—' },
                { key: 'completed', label: 'Hoàn tất lúc', children: activeJob.completed_at ? formatDateTime(activeJob.completed_at) : '—' },
                { key: 'success', label: 'Thành công', children: formatNumber(activeJob.success_rows) },
                { key: 'warning', label: 'Cảnh báo', children: formatNumber(activeJob.warning_rows) },
                { key: 'error', label: 'Lỗi', children: formatNumber(activeJob.error_rows) },
              ]}
            />

            {errorRows.length > 0 ? (
              <>
                <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                  <Text strong>Chi tiết lỗi / cảnh báo</Text>
                  <Button icon={<DownloadOutlined />} onClick={handleDownloadErrorCsv}>
                    Tải CSV lỗi
                  </Button>
                </Space>
                <ERPTable<ImportValidationIssue>
                  tableKey={`import-errors-${entity}`}
                  size="small"
                  pagination={false}
                  hideSettingsButton
                  dataSource={errorRows}
                  rowKey={(row) => `${row.row}-${row.column}-${row.message}`}
                  columns={errorColumns}
                />
              </>
            ) : (
              <Empty description="Không có lỗi cần hiển thị" />
            )}

            <Space>
              <Button onClick={() => {
                if (embedded) {
                  onClose?.();
                  return;
                }
                navigate(ROUTES.imports.index);
              }}>
                {embedded ? 'Đóng' : 'Quay lại hub'}
              </Button>
              <Button type="primary" onClick={resetWizard}>Import file khác</Button>
            </Space>
          </Space>
        )}
      </Card>

      <Modal
        title="Lưu mẫu ánh xạ"
        open={saveTemplateOpen}
        onCancel={() => setSaveTemplateOpen(false)}
        onOk={() => {
          void handleSaveTemplate();
        }}
        confirmLoading={createTemplateMutation.isPending}
        okText="Lưu mẫu"
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          <div>
            <Text strong>Tên mẫu</Text>
            <Input
              placeholder="Ví dụ: KiotViet sản phẩm - cửa hàng Q1"
              value={templateName}
              onChange={(event) => setTemplateName(event.target.value)}
            />
          </div>
          <div>
            <Text strong>Hệ nguồn</Text>
            <Select
              style={{ width: '100%' }}
              value={templateSourceSystem}
              options={[...TEMPLATE_SOURCE_OPTIONS]}
              onChange={setTemplateSourceSystem}
            />
          </div>
        </Space>
      </Modal>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <PageShell
      title={config.title}
      extra={<Button onClick={() => navigate(ROUTES.imports.index)}>Quay lại</Button>}
    >
      {content}
    </PageShell>
  );
}
